import { useState } from 'react'
import { Button, ScrollArea } from '@creanizer/ui'
import { Trash2, Copy, Check, Folder } from 'lucide-react'
import type { SavedName } from '@creanizer/types'
import type { CharacterInput, CharacterResult, SavedCharacter } from '../../../../types/character' // temp fix
import { ExportImportButtons } from '../../../../components/common'
import { useFolderStore } from '../../../../stores/folderStore'
import { FolderManager, FolderMoveSelect } from './FolderManager'

interface SavedNamesListProps {
  savedNames: SavedName[]
  exportAsText: () => void
  exportAsJSON: () => void
  importFromJSON: (jsonString: string) => void
  clearAllNames: () => void
  deleteName: (id: string) => void
}

export function SavedNamesList({
  savedNames,
  exportAsText,
  exportAsJSON,
  importFromJSON,
  clearAllNames,
  deleteName,
}: SavedNamesListProps) {
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null)
  const [showFolders, setShowFolders] = useState(true)
  const [draggedItemId, setDraggedItemId] = useState<string | null>(null)
  
  const { getItemsInFolder, getItemFolder, moveItemBetweenFolders } = useFolderStore()
  
  // フォルダによるフィルタリング
  let filteredNames = savedNames
  if (selectedFolderId === 'all') {
    filteredNames = savedNames
  } else if (selectedFolderId === null) {
    // 未整理: どのフォルダにも属していないアイテム
    const allFolderItemIds = new Set<string>()
    const folders = useFolderStore.getState().getFolders('name')
    folders.forEach(folder => {
      getItemsInFolder('name', folder.id).forEach(id => allFolderItemIds.add(id))
    })
    filteredNames = savedNames.filter(name => !allFolderItemIds.has(name.id))
  } else {
    const itemIds = getItemsInFolder('name', selectedFolderId)
    filteredNames = savedNames.filter(name => itemIds.includes(name.id))
  }
  
  const handleCopy = async (name: SavedName) => {
    try {
      await navigator.clipboard.writeText(name.name)
      setCopiedId(name.id)
      setTimeout(() => setCopiedId(null), 2000)
    } catch (err) {
      console.error('Failed to copy name:', err)
    }
  }
  
  const typeLabels: Record<string, string> = {
    person_full: '人名（フル）',
    person_family: '人名（姓）',
    person_given: '人名（名）',
    weapon: '武器',
    skill: 'スキル',
    place: '地名',
    organization: '組織',
    item: 'アイテム',
    magic: '魔法',
    title: '称号',
    enemy: 'エネミー',
  }

  return (
    <div className="card">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">保存済み名前 ({savedNames.length}/1000)</h3>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFolders(!showFolders)}
            className="px-2"
          >
            <Folder className="h-4 w-4" />
          </Button>
          <ExportImportButtons
            onExportText={exportAsText}
            onExportJSON={exportAsJSON}
            onImportJSON={importFromJSON}
            dataTypeName="名前"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={clearAllNames}
            disabled={savedNames.length === 0}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            すべて削除
          </Button>
        </div>
      </div>
      
      <div className="flex gap-4">
        {/* フォルダサイドバー */}
        {showFolders && (
          <div className="w-48 border-r pr-4">
            <FolderManager
              generatorType="name"
              items={savedNames}
              selectedFolderId={selectedFolderId}
              onFolderSelect={setSelectedFolderId}
              draggedItemId={draggedItemId}
              onItemMove={(itemId, toFolderId) => {
                const currentFolder = getItemFolder('name', itemId)
                moveItemBetweenFolders('name', itemId, currentFolder?.id || null, toFolderId)
              }}
            />
          </div>
        )}
        
        {/* 名前リスト */}
        <div className="flex-1">
          {filteredNames.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">
              保存された名前はありません
            </p>
          ) : (
            <ScrollArea className="h-[600px]">
              <div className="space-y-2">
                {filteredNames.map((name) => (
                  <div
                    key={name.id}
                    className="flex items-center justify-between p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors cursor-move"
                    draggable
                    onDragStart={(e) => {
                      setDraggedItemId(name.id)
                      e.dataTransfer.effectAllowed = 'move'
                    }}
                    onDragEnd={() => {
                      setDraggedItemId(null)
                    }}
                    style={{
                      opacity: draggedItemId === name.id ? 0.5 : 1
                    }}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{name.name}</span>
                        {name.reading && (
                          <span className="text-sm text-muted-foreground">({name.reading})</span>
                        )}
                        <span className="text-xs bg-background px-2 py-0.5 rounded">
                          {typeLabels[name.nameType] || name.nameType}
                        </span>
                      </div>
                      {name.meaning && (
                        <div className="text-xs text-muted-foreground mt-1">{name.meaning}</div>
                      )}
                      <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                        <span>{name.style}</span>
                        <span>•</span>
                        <span>珍しさ: {name.rarity}/10</span>
                        {name.tags && name.tags.length > 0 && (
                          <>
                            <span>•</span>
                            <span>{name.tags.join(', ')}</span>
                          </>
                        )}
                      </div>
                      {/* フォルダ選択 */}
                      <div className="mt-2 w-48">
                        <FolderMoveSelect
                          generatorType="name"
                          itemId={name.id}
                          currentFolderId={getItemFolder('name', name.id)?.id}
                          onMove={() => {}}
                        />
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleCopy(name)}
                        title="コピー"
                      >
                        {copiedId === name.id ? (
                          <Check className="h-4 w-4 text-green-600" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => deleteName(name.id)}
                        title="削除"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </div>
      </div>
    </div>
  )
}